import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { captureAttribution } from './attribution';

// #57 — code-split each entry app. index.js mounts exactly one of these by
// pathname, but static imports forced ALL of them (incl. the ~944 KB finale
// app + full admin) into a single bundle, so a visitor to /booking/xyz
// downloaded everything. lazy() gives each entry its own chunk: each pathname
// now ships only the app it renders.
const App = lazy(() => import('./aura-cash-FINAL'));
const AdminApp = lazy(() => import('./admin/App'));
const StaffApp = lazy(() => import('./staff/App'));
const Valoracion = lazy(() => import('./valoracion'));
const Checkin = lazy(() => import('./checkin'));
const BookingLink = lazy(() => import('./booking-link'));
const Redeem = lazy(() => import('./redeem'));

// Minimal branded fallback shown while an entry chunk loads.
const Loader = () => <div style={{ minHeight: '100vh', background: '#0F0D0A' }} />;

// First-touch ad attribution: read fbclid / gclid / utm_* off the landing
// URL and persist them to localStorage before anything renders, so the
// booking flow can forward them onto the customer record. Best-effort —
// never let an attribution hiccup block the app from mounting.
try { captureAttribution(); } catch { /* private mode / SSR — ignore */ }

const root = ReactDOM.createRoot(document.getElementById('root'));

// Admin mode is reachable two ways so we degrade gracefully:
//  1. /admin/* path — works when Vercel's rewrite is wired
//  2. #admin hash — share-friendly fallback
const isStaff =
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/staff");

const isAdmin =
  typeof window !== "undefined" &&
  (window.location.pathname.startsWith("/admin") ||
    window.location.hash === "#admin" ||
    localStorage.getItem("aura_admin_mode") === "1");

// Public lead-capture landing.
const isValoracion =
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/valoracion");

// Public self check-in landing.
const isCheckin =
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/checkin");

// Public booking-link landing (self cancel / reschedule).
const isBookingLink =
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/booking/");

// Public reactivation-gift redemption landing (cold campaign).
const isRedeem =
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/redeem/");

// Surface missing build-time config loudly instead of letting the app
// limp into "Error de conexión" on every auth/data call. If the Vercel
// project's env vars weren't pulled into the build, the bundle ships
// with SUPABASE_URL/SUPABASE_ANON_KEY === undefined and every fetch
// hits `undefined/...` which 404s back HTML and crashes JSON parsing.
if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
  // eslint-disable-next-line no-console
  console.error(
    "[aura] Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY. " +
    "The app was built without Supabase credentials. " +
    "Set them in Vercel → Project Settings → Environment Variables (Production) and redeploy."
  );
  root.render(
    <div style={{minHeight:"100vh",background:"#0F0D0A",color:"#C9A96E",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,fontFamily:"system-ui,sans-serif",textAlign:"center"}}>
      <h1 style={{fontSize:20,marginBottom:12}}>Configuración faltante</h1>
      <p style={{fontSize:14,maxWidth:420,lineHeight:1.5,color:"rgba(201,169,110,0.7)"}}>
        Esta versión se desplegó sin las credenciales de Supabase.
        Configura <code>REACT_APP_SUPABASE_URL</code> y <code>REACT_APP_SUPABASE_ANON_KEY</code> en Vercel y vuelve a desplegar.
      </p>
    </div>
  );
} else {
  const Entry =
    isStaff       ? StaffApp :
    isAdmin       ? AdminApp :
    isValoracion  ? Valoracion :
    isCheckin     ? Checkin :
    isBookingLink ? BookingLink :
    isRedeem      ? Redeem :
                    App;
  root.render(
    <React.StrictMode>
      <Suspense fallback={<Loader />}>
        <Entry />
      </Suspense>
    </React.StrictMode>,
  );
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
      if (window.caches) {
        const keys = await window.caches.keys();
        await Promise.all(keys.filter((key) => key.startsWith('aura-')).map((key) => window.caches.delete(key)));
      }
    } catch {
      // Cache cleanup is best-effort; the app must keep loading normally.
    }
  });
}
